
import { css } from "@emotion/react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ReactComponent as Logo } from "../../../assets/img/logoSmall.svg";
import {
  THERAPIST_NAVIGATION_ITEMS,
  ROLES,
  ROUTES,
  SERVICES,
} from "@app/constants";
import NavIcon from "./NavIcon";
import { getViewFromURL } from "@amplio-common/funcs/commonFunctionalities";
import BottomPanel from "./BottomPanel";
import { useDispatch, useSelector } from "react-redux";
// import { getTreatment } from "@app/routes/treatment/selectors";
import { selectTreatment } from "@app/routes/treatmentV3/store/treatmentServerDataSlice";
import { currentUser } from "@app/routes/auth/selectors";
import Snow from "./Snow";
import { Trans } from "@lingui/macro";
import CommonDialog from "@amplio-common/components/Dialog";
import { ReactComponent as Union } from "@app/assets/svgs/union.svg";
import { selectSelfPracticeGame, setCurrentSelfPractice, setSelfPracticeGame } from "@app/routes/treatmentV3/store/studentSlice";
import { studentSelfPracticeUpdate } from "@app/routes/assignments/api";
import { setIsSelfPracticeDialogOpen } from "@app/routes/treatmentV3/store/contentSlice";
import moment from "moment-timezone";
import { DATETIME_UTC_FORMAT } from "@amplio-common/constants";
import { wsClose } from "@app/routes/treatmentV3/store/websocketSlice";

const rootStyle = theme => css`
  height: 100%;
  width: 3.5rem;
  background-color: ${theme.colors.primary.dark};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  position: relative;
  right: 0 !important;
  z-index: 1500;

  .top-navigation {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .points-of-interest {
    display: flex;
    flex-direction: column;
    gap: 25px;
    margin-top: 26px;
  }
`;

const LeftSideNavigation = ({ history }) => {
  const urlView = getViewFromURL();
  const dispatch = useDispatch();
  // const remoteTreatmentId = useSelector(getTreatment)?.id;
  const classV3TreatmentId = useSelector(selectTreatment)?.id;
  const currUser = useSelector(currentUser);
  const classTreatmentId = classV3TreatmentId;
  const view = currUser.is_amplio_employee ? ROUTES.CALENDAR : ROUTES.CLASSROOM;
  const [currentView, setCurrentView] = useState(urlView || view);
  const [toView, setToView] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [toPath, setToPath] = useState("");

  const urlSuffix = (classTreatmentId && `/${classTreatmentId}`) || "";
  const showResources = currUser?.cert_area_of_need?.length > 0 && currUser?.services?.includes(SERVICES.DYSLEXIA.id); // temp condition. review in the future

  const { pathname = "" } = history.location || {};
  // eslint-disable-next-line
  const [path1, feature, version, treatmentId] = pathname.split("/");

  const roleCond = [ROLES.THERAPIST.id, ROLES.TRAINER.id].includes(currUser.role);
  const isTherapist = [ROLES.THERAPIST.id].includes(currUser.role);
  const isStudent = [ROLES.STUDENT.id].includes(currUser.role);

  const therapistOnSession = feature === "treatment" && version === "v3" && treatmentId && urlSuffix;
  const currentSelfPracticeGame = useSelector(selectSelfPracticeGame);

  const filterTabs = (tab) => {
    if (tab.path === ROUTES.RESOURCES) {
      return showResources;
    }
    if (tab.path === ROUTES.TIMESHEET) {
      return (currUser.is_amplio_employee || currUser.is_tipuli_employee);
    }
    return true;
  };
  const filteredNavItems = THERAPIST_NAVIGATION_ITEMS.filter(filterTabs);

  const changeActiveIcon = (e, type) => {
    if (!type) type = view;
    if (therapistOnSession) {
      setToView(type);
      return;
    }
    setCurrentView(type);
  };

  const addSuffix = path => [ROUTES.CLASSROOM_V3, ROUTES.CLASSROOM_V4].includes(path) ? urlSuffix : "";

  useEffect(() => {
    const unlisten = history.listen(() => {
      !isTherapist && setCurrentView(urlView);
    });
    isTherapist && setCurrentView(urlView);

    return () => {
      unlisten();
    };
  }, [urlView, history]);

  const handleConfirmNavigation = () => {
    dispatch(wsClose());
    history.push(toPath);
    setToPath("");
    setShowDialog(false);
    setCurrentView(toView);
  };

  const handleNavigation = (e, path) => {
    dispatch(setIsSelfPracticeDialogOpen(false));
    if (therapistOnSession) {
      e.preventDefault();
      setToPath(path);
      path !== "#" && setShowDialog(true);
    }

    if (currentSelfPracticeGame) {
      const { id, treatment_id, student_id } = currentSelfPracticeGame || {};
      studentSelfPracticeUpdate({ treatment_id, student_id, game_id: id, game_ended: moment().format(DATETIME_UTC_FORMAT) });
    }
    dispatch(setSelfPracticeGame(null));
    dispatch(setCurrentSelfPractice(null));
  };

  return (
    <div css={rootStyle}>
      {showDialog && (
        <CommonDialog
          style={{ zIndex: 1500 }}
          open={showDialog}
          cancelAction={() => setShowDialog(false)}
          title={<Trans>Leave session?</Trans>}
          okAction={handleConfirmNavigation}
          maxWidth="xl"
          noCloseOnFocusOut
          okLabel={<Trans>Yes</Trans>}
          cancelLabel={<Trans>No</Trans>}
          content={(<Trans style={{ fontSize: 14 }}>Are you sure you want to leave the session?</Trans>)}
        />
      )}
      <Snow />
      <div className="top-navigation">
        <Link
          to="/"
          className="poppins-logo"
          data-testid="amplio-logo"
          onClick={(e) => handleNavigation(e, "/")}
        >
          <Logo onClick={changeActiveIcon} />
        </Link>
        <div className="points-of-interest">
          {roleCond && filteredNavItems.map(i => (
            <Link
              to={i.path === currentView ? "#" : `${i.path}${addSuffix(i.path)}`}
              key={i.name}
              data-testid={i.name.replace(/ +/g, "-")}
              onClick={(e) => handleNavigation(e, i.path === currentView ? "#" : `${i.path}${addSuffix(i.path)}`)}
            >
              <NavIcon
                isActive={i.path === currentView}
                icon={i.icon}
                onClick={changeActiveIcon}
                type={i.path}
                name={<Trans id={i.name} values={i.name} />}
              />
            </Link>
          ))}
          {isStudent && (
            <Link
              to={ROUTES.SELF_PRACTICE === currentView ? "#" : ROUTES.SELF_PRACTICE}
              key="Self Practice"
              onClick={(e) => handleNavigation(e, ROUTES.SELF_PRACTICE === currentView ? "#" : ROUTES.SELF_PRACTICE)}
            >
              <NavIcon
                disableFill
                isActive={window.location.href.includes(ROUTES.SELF_PRACTICE)}
                icon={<Union />}
                onClick={changeActiveIcon}
                type={ROUTES.SELF_PRACTICE}
                name={<Trans id="Self practice" values="Self Practice" />}
              />
            </Link>
          )}
        </div>
      </div>
      <BottomPanel />
    </div>
  );
};

export default LeftSideNavigation;
